
import { useState } from 'react';

interface Project {
  title: string;
  description: string;
  beforeImage: string;
  afterImage: string;
  metrics: string[];
  galleryImages?: { src: string; caption: string }[];
}

const projects: Project[] = [
  {
    title: 'Structured Cabling Upgrade',
    description:
      'Transformed disorganized network cabling into a clean, labeled, scalable infrastructure environment.',
    beforeImage:
      'https://static.readdy.ai/image/51b92d7fefcf4905c242441f3c7dddfe/d0aff1c84a0f0be4f8839436def8d08a.png',
    afterImage:
      'https://static.readdy.ai/image/51b92d7fefcf4905c242441f3c7dddfe/2d6d845e94ac0fad8d87214120a37f9e.png',
    metrics: ['200+ cables organized', '100% labeled infrastructure', 'Zero downtime migration'],
  },
  {
    title: 'Office Workstation Rollout',
    description:
      'Full deployment and configuration of multi-user workstation setup, ready for immediate productivity.',
    beforeImage:
      'https://static.readdy.ai/image/51b92d7fefcf4905c242441f3c7dddfe/0ae2df7551426c38861701dedf6b15b2.png',
    afterImage:
      'https://static.readdy.ai/image/51b92d7fefcf4905c242441f3c7dddfe/dcbb3cdfbc622a66da7128f3f5d92907.png',
    metrics: ['75 workstations deployed', 'Quick turnaround', 'Dual monitor configuration'],
  },
  {
    title: 'Enterprise Workstation Deployment',
    description:
      'Complete rollout and configuration of multi-user office infrastructure, delivered with precision and operational readiness.',
    beforeImage:
      'https://static.readdy.ai/image/51b92d7fefcf4905c242441f3c7dddfe/cd53d59ea98d13b1ac85649ca07e20f3.png',
    afterImage:
      'https://static.readdy.ai/image/51b92d7fefcf4905c242441f3c7dddfe/4c1a6eaa85637d23949d37a91d55fe08.png',
    metrics: ['130+ Workstations Deployed', '48-Hour Deployment Window', 'Zero Post-Deployment Issues'],
    galleryImages: [
      {
        src: 'https://static.readdy.ai/image/51b92d7fefcf4905c242441f3c7dddfe/30eff748d7515d7c6e40d24628f34d37.png',
        caption: 'Workstation rows fully configured and ready for use',
      },
      {
        src: 'https://static.readdy.ai/image/51b92d7fefcf4905c242441f3c7dddfe/6256b3c726ac621c92fcf6617f62c359.png',
        caption: 'Dual monitor docking station configuration',
      },
      {
        src: 'https://static.readdy.ai/image/51b92d7fefcf4905c242441f3c7dddfe/fee35d2d09921fda00f2c6dc03228b06.png',
        caption: 'Deployment team executing on-site rollout',
      },
      {
        src: 'https://static.readdy.ai/image/51b92d7fefcf4905c242441f3c7dddfe/edaec6b6133d3097db1f4683e2c08488.png',
        caption: 'Equipment staging and preparation area',
      },
      {
        src: 'https://static.readdy.ai/image/51b92d7fefcf4905c242441f3c7dddfe/428d7dd97358d35ea277752b91391204.png',
        caption: 'Completed floor with all workstations operational',
      },
      {
        src: 'https://static.readdy.ai/image/51b92d7fefcf4905c242441f3c7dddfe/1513a3121444625d5fdff2da2d7c41ea.jpeg',
        caption: 'Clean under-desk cable management',
      },
    ],
  },
];

function ProjectCard({ project, index }: { project: Project; index: number }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [editMode, setEditMode] = useState(false);

  const handleImageUpload = (imageIndex: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newSrc = e.target?.result as string;
        // In a real implementation, you would upload to your server here
        // For now, we'll show an alert with instructions
        alert(
          `To replace this image:\n\n1. Upload your image to Readdy's asset manager\n2. Copy the image URL\n3. Contact support or your developer to update the gallery with the new URL\n\nSelected file: ${file.name}`
        );
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="bg-zinc-800/50 rounded-lg border border-zinc-700/50 overflow-hidden hover:border-orange-500/20 transition-all duration-500">
      {/* Images */}
      <div className="grid grid-cols-2 gap-0 relative">
        <div className="relative group">
          <div className="w-full h-64 overflow-hidden">
            <img
              src={project.beforeImage}
              alt={`${project.title} - Before`}
              title={`${project.title} before IT deployment`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            />
          </div>
        </div>
        <div className="relative group">
          <div className="w-full h-64 overflow-hidden">
            <img
              src={project.afterImage}
              alt={`${project.title} - After`}
              title={`${project.title} after IT deployment`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            />
          </div>
        </div>

        {/* Divider */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
          <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-full shadow-lg">
            <i className="ri-arrow-left-right-line text-white text-lg"></i>
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      {project.galleryImages && project.galleryImages.length > 0 && (
        <div className="px-6 pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-gallery-line text-orange-500 text-base"></i>
              </div>
              <span className="text-orange-500 text-xs font-semibold uppercase tracking-widest">
                Project Gallery&nbsp;
              </span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {project.galleryImages.map((img, i) => (
              <div
                key={i}
                className="relative group rounded-md overflow-hidden border border-zinc-700/40 hover:border-orange-500/30 transition-all duration-300"
              >
                <div 
                  className={`w-full h-40 overflow-hidden ${!editMode ? 'cursor-pointer' : ''}`}
                  onClick={() => !editMode && setLightboxIndex(i)}
                >
                  <img
                    src={img.src}
                    alt={img.caption}
                    title={img.caption}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                
                {/* View/Zoom Overlay (only when not in edit mode) */}
                {!editMode && (
                  <>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center pointer-events-none">
                      <div className="w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <i className="ri-zoom-in-line text-white text-xl"></i>
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2.5 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <p className="text-white text-xs leading-snug">{img.caption}</p>
                    </div>
                  </>
                )}

                {/* Replace Button Overlay (only in edit mode) */}
                {editMode && (
                  <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2">
                    <label className="flex flex-col items-center gap-2 cursor-pointer">
                      <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-full hover:bg-orange-600 transition-colors">
                        <i className="ri-upload-2-line text-white text-lg"></i>
                      </div>
                      <span className="text-white text-xs font-semibold">Replace</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(i, e)}
                        className="hidden"
                      />
                    </label>
                    <p className="text-zinc-300 text-[10px] text-center px-2 leading-tight">
                      {img.caption}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Edit Mode Instructions */}
          {editMode && (
            <div className="mt-4 p-4 bg-orange-500/5 border border-orange-500/20 rounded-md">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-orange-500 text-base"></i>
                </div>
                <div className="flex-1">
                  <p className="text-orange-400 text-xs font-semibold mb-1">How to Replace Images:</p>
                  <ol className="text-zinc-400 text-xs leading-relaxed space-y-1 list-decimal list-inside">
                    <li>Click "Replace" on any gallery image</li>
                    <li>Upload your image to Readdy's asset manager</li>
                    <li>Copy the generated image URL</li>
                    <li>Share the URL with your developer to update the gallery</li>
                  </ol>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && project.galleryImages && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex(null);
            }}
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center bg-zinc-800 rounded-full text-white hover:bg-zinc-700 transition-colors cursor-pointer z-50"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex(
                lightboxIndex > 0 ? lightboxIndex - 1 : project.galleryImages!.length - 1
              );
            }}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-zinc-800/80 rounded-full text-white hover:bg-orange-500 transition-colors cursor-pointer z-50"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex(
                lightboxIndex < project.galleryImages!.length - 1 ? lightboxIndex + 1 : 0
              );
            }}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-zinc-800/80 rounded-full text-white hover:bg-orange-500 transition-colors cursor-pointer z-50"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>

          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="w-full h-[500px] rounded-lg overflow-hidden">
              <img
                src={project.galleryImages[lightboxIndex].src}
                alt={project.galleryImages[lightboxIndex].caption}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="text-center mt-4">
              <p className="text-white text-sm">{project.galleryImages[lightboxIndex].caption}</p>
              <p className="text-zinc-500 text-xs mt-1">
                {lightboxIndex + 1} of {project.galleryImages.length}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-white mb-2">{project.title}</h3>
        <p className="text-zinc-400 text-sm leading-relaxed mb-5">{project.description}</p>

        {/* Metrics */}
        <div className="flex flex-wrap gap-2">
          {project.metrics.map((metric, i) => (
            <span
              key={i}
              className="text-xs font-medium text-orange-400 bg-orange-500/10 px-3 py-1.5 rounded-md border border-orange-500/10"
            >
              {metric}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Portfolio() {
  const scrollToContact = () => {
    const section = document.getElementById('contact-section');
    section?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="portfolio-section" className="py-24 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-orange-500 text-sm font-semibold uppercase tracking-widest mb-4 block">
            Our Work
          </span>
          <h2 className="text-4xl font-bold text-white mb-5">Deployment Execution in Action</h2>
          <p className="text-zinc-400 text-base leading-relaxed max-w-3xl mx-auto">
            Real-world examples of structured IT deployments and infrastructure transformations delivered by
            ZJF IT Services LLC.
          </p>
        </div>

        {/* Project Cards */}
        <div className="space-y-10">
          {projects.map((project, index) => (
            <ProjectCard key={index} project={project} index={index} />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-zinc-500 text-sm mb-5">Need similar deployment support?</p>
          <button
            onClick={scrollToContact}
            className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-md text-base font-semibold transition-all duration-300 shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 whitespace-nowrap cursor-pointer"
          >
            Request a Consultation
          </button>
        </div>
      </div>
    </section>
  );
}
